import type { OutgoingStatisticsResponse } from '../../../store/reducers/OutgoingStatisticsSlice';

import type { OutgoingStatisticMetrics } from './outgoingStatisticMetrics';
import type { OutgoingStatisticRow } from './statisticRowTypes';
import {
  getSelectedScenarioVariant,
  type ScenarioVariant,
} from './scenarioMetadata';
import { createMetricRow, createSectionRow } from './statisticRowFactory';

const buildSlotInteractionRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => {
  const section = 'Результаты взаимодействия по обработанным заданиям';
  const variant: ScenarioVariant = 'slot';
  const cancelledTotal =
    statistic.cancelledTasksCount +
    statistic.rescheduledTasksCount +
    statistic.operatorHandedTasksCount +
    statistic.rescheduledByClinicTasksCount +
    statistic.cancelledByClinicTasksCount;
  const cancelledPatientAndClinic =
    statistic.cancelledTasksCount + statistic.cancelledByClinicTasksCount;
  const rescheduledPatientAndClinic =
    statistic.rescheduledTasksCount + statistic.rescheduledByClinicTasksCount;

  return [
    createMetricRow({
      key: 'interaction-confirmedTasksCount',
      section,
      parameter: 'Подтверждено приемов',
      value: statistic.confirmedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key: 'interaction-slot-cancelledTotal',
      section,
      parameter:
        'Отменено приемов (в том числе перенесено и передано оператору)',
      value: cancelledTotal,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key: 'interaction-slot-cancelledPatientAndClinic',
      section,
      parameter: 'Отменено приемов (пациентом и МО)',
      value: cancelledPatientAndClinic,
      denominator: cancelledTotal,
      level: 1,
      variant,
    }),
    createMetricRow({
      key: 'interaction-cancelledTasksCount',
      section,
      parameter: 'Отменено пациентом',
      value: statistic.cancelledTasksCount,
      denominator: cancelledPatientAndClinic,
      level: 2,
      variant,
    }),
    createMetricRow({
      key: 'interaction-cancelledByClinicTasksCount',
      section,
      parameter: 'Отменено МО',
      value: statistic.cancelledByClinicTasksCount,
      denominator: cancelledPatientAndClinic,
      level: 2,
      variant,
    }),
    createMetricRow({
      key: 'interaction-slot-rescheduledPatientAndClinic',
      section,
      parameter: 'Перенесено приемов (пациентом и МО)',
      value: rescheduledPatientAndClinic,
      denominator: cancelledTotal,
      level: 1,
      variant,
    }),
    createMetricRow({
      key: 'interaction-rescheduledTasksCount',
      section,
      parameter: 'Перенесено пациентом',
      value: statistic.rescheduledTasksCount,
      denominator: rescheduledPatientAndClinic,
      level: 2,
      variant,
    }),
    createMetricRow({
      key: 'interaction-rescheduledByClinicTasksCount',
      section,
      parameter: 'Перенесено МО',
      value: statistic.rescheduledByClinicTasksCount,
      denominator: rescheduledPatientAndClinic,
      level: 2,
      variant,
    }),
    createMetricRow({
      key: 'interaction-operatorHandedTasksCount',
      section,
      parameter: 'Передано оператору',
      value: statistic.operatorHandedTasksCount,
      denominator: metrics.processedTasksCount,
      level: 1,
      variant,
      isEmphasized: true,
    }),
    createMetricRow({
      key: 'interaction-notIdentifiedTasksCount',
      section,
      parameter: 'Не удалось продолжить диалог - абонент не идентифицирован',
      value: statistic.notIdentifiedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key: 'interaction-notReceivedTasksCount',
      section,
      parameter: 'Не удалось дозвониться',
      value: statistic.failedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
  ];
};

const buildSimpleInteractionRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
  variant: Exclude<ScenarioVariant, 'default' | 'slot' | 'informing'>,
): OutgoingStatisticRow[] => {
  const section = 'Результаты взаимодействия';
  const labelsByVariant: Record<
    Exclude<ScenarioVariant, 'default' | 'slot' | 'informing'>,
    readonly [string, string, string, string, string]
  > = {
    dispensarization: [
      'Согласились на диспансеризацию',
      'Отказались от диспансеризации',
      'Передано оператору',
      'Не удалось продолжить диалог - абонент не идентифицирован',
      'Не получено ответов (недозвон)',
    ],
    questionnaire: [
      'Пройдено опросов',
      'Отказались от прохождения',
      'Передано оператору',
      'Не удалось продолжить диалог - абонент не идентифицирован',
      'Не получено ответов (недозвон)',
    ],
    appointment: [
      'Успешно записалось на прием',
      'Отказалось от записи на прием',
      'Передано оператору',
      'Не удалось продолжить диалог - абонент не идентифицирован',
      'Не получено ответов',
    ],
  };
  const [
    confirmedLabel,
    cancelledLabel,
    operatorLabel,
    notIdentifiedLabel,
    notReceivedLabel,
  ] = labelsByVariant[variant];

  return [
    createMetricRow({
      key: 'interaction-confirmedTasksCount',
      section,
      parameter: confirmedLabel,
      value: statistic.confirmedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key: 'interaction-cancelledTasksCount',
      section,
      parameter: cancelledLabel,
      value: statistic.cancelledTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    ...(variant === 'appointment'
      ? [
          createMetricRow({
            key: 'interaction-waitingListTasksCount',
            section,
            parameter: 'Записалось в лист ожидания',
            value: statistic.waitingListTasksCount,
            denominator: metrics.processedTasksCount,
            variant,
          }),
        ]
      : []),
    createMetricRow({
      key: 'interaction-operatorHandedTasksCount',
      section,
      parameter: operatorLabel,
      value: statistic.operatorHandedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key: 'interaction-notIdentifiedTasksCount',
      section,
      parameter: notIdentifiedLabel,
      value: statistic.notIdentifiedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key:
        variant === 'dispensarization'
          ? 'interaction-dispensarization-notReceivedTasksCount'
          : 'interaction-notReceivedTasksCount',
      section,
      parameter: notReceivedLabel,
      value:
        variant === 'dispensarization'
          ? statistic.notReceivedTasksCount + statistic.failedTasksCount
          : statistic.failedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
  ];
};

const buildInformingInteractionRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => {
  const section = 'Результаты взаимодействия';
  const variant: ScenarioVariant = 'informing';

  return [
    createMetricRow({
      key: 'interaction-confirmedTasksCount',
      section,
      parameter: 'Информация прослушана',
      value: statistic.confirmedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
    createMetricRow({
      key: 'interaction-notReceivedTasksCount',
      section,
      parameter: 'Информация не прослушана (недозвон)',
      value: statistic.failedTasksCount,
      denominator: metrics.processedTasksCount,
      variant,
    }),
  ];
};

export const buildInteractionSection = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
  selectedScenario: string | null,
): OutgoingStatisticRow | null => {
  const variant = getSelectedScenarioVariant(selectedScenario);

  if (variant === 'default') {
    return null;
  }

  if (variant === 'slot') {
    return createSectionRow({
      key: 'interaction',
      section: 'Результаты взаимодействия по обработанным заданиям',
      children: buildSlotInteractionRows(statistic, metrics),
      variant,
    });
  }

  if (variant === 'informing') {
    return createSectionRow({
      key: 'interaction',
      section: 'Результаты взаимодействия',
      children: buildInformingInteractionRows(statistic, metrics),
      variant,
    });
  }

  return createSectionRow({
    key: 'interaction',
    section: 'Результаты взаимодействия',
    children: buildSimpleInteractionRows(statistic, metrics, variant),
    variant,
  });
};
